import jsPDF from 'jspdf';
/**
 * Parse HTML and render it to PDF with formatting
 * @param {jsPDF} doc - The PDF document instance
 * @param {string} html - HTML content from rich text editor
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {object} options - Rendering options
 * @returns {number} - Height consumed by the rendered content
 */
const renderHtmlToPdf = (doc, html, x, y, options = {}) => {
  if (!html || html === '<p><br></p>') return 0;

  console.log(`[Treatment Plan PDF] Rendering HTML content with formatting`);

  const {
    fontSize = 10,
    maxWidth = doc.internal.pageSize.getWidth() - 40,
    lineHeightMultiplier = 1.5,
  } = options;

  const lineHeight = fontSize * lineHeightMultiplier;

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  let currentY = y;
  let totalHeight = 0;

  // Extract text with formatting from a node
  const extractFormattedText = (node, currentFormat = {}) => {
    const segments = [];

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text && text.trim()) {
        segments.push({ text, format: { ...currentFormat } });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      const newFormat = { ...currentFormat };

      // Update format based on tag
      if (tagName === 'strong' || tagName === 'b') {
        newFormat.bold = true;
      } else if (tagName === 'em' || tagName === 'i') {
        newFormat.italic = true;
      } else if (tagName === 'u') {
        newFormat.underline = true;
      }

      // Process children
      node.childNodes.forEach(child => {
        segments.push(...extractFormattedText(child, newFormat));
      });
    }

    return segments;
  };

  // Render formatted text segments
  const renderFormattedSegments = (segments, xPos, yPos, indent = 0, listMarker = null) => {
    if (segments.length === 0) return 0;

    const availableWidth = maxWidth - indent;
    let linesRendered = 0;
    let currentLineSegments = [];
    let currentLineWidth = 0;

    // Add list marker if this is a list item
    if (listMarker) {
      doc.setFont('helvetica', 'normal');
      doc.text(listMarker, xPos + indent - 10, yPos);
    }

    // Helper to render a line of segments
    const renderLine = () => {
      if (currentLineSegments.length === 0) return;

      let xOffset = xPos + indent;
      currentLineSegments.forEach(seg => {
        const { text, format } = seg;

        // Set font based on format
        let fontStyle = 'normal';
        if (format.bold && format.italic) {
          fontStyle = 'bolditalic';
        } else if (format.bold) {
          fontStyle = 'bold';
        } else if (format.italic) {
          fontStyle = 'italic';
        }
        doc.setFont('helvetica', fontStyle);

        doc.text(text, xOffset, yPos + (linesRendered * lineHeight));
        xOffset += doc.getStringUnitWidth(text) * fontSize / doc.internal.scaleFactor;
      });

      linesRendered++;
      currentLineSegments = [];
      currentLineWidth = 0;
    };

    // Process each segment
    segments.forEach((segment, segIndex) => {
      const { text, format } = segment;

      // Set font to measure text width correctly
      let fontStyle = 'normal';
      if (format.bold && format.italic) {
        fontStyle = 'bolditalic';
      } else if (format.bold) {
        fontStyle = 'bold';
      } else if (format.italic) {
        fontStyle = 'italic';
      }
      doc.setFont('helvetica', fontStyle);

      // Split text into words
      const words = text.split(/(\s+)/); // Keep whitespace

      words.forEach((word, wordIndex) => {
        if (!word) return;

        const wordWidth = doc.getStringUnitWidth(word) * fontSize / doc.internal.scaleFactor;

        // Check if adding this word would exceed line width
        if (currentLineWidth + wordWidth > availableWidth && currentLineSegments.length > 0) {
          renderLine();
        }

        // Add word to current line
        currentLineSegments.push({ text: word, format });
        currentLineWidth += wordWidth;
      });
    });

    // Render final line
    if (currentLineSegments.length > 0) {
      renderLine();
    }

    return linesRendered * lineHeight;
  };

  // Process nodes recursively
  const processNode = (node, indent = 0, listItemNumber = null) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();

      if (tagName === 'p') {
        // Extract and render formatted text from paragraph
        const segments = extractFormattedText(node);
        if (segments.length > 0) {
          const height = renderFormattedSegments(segments, x, currentY, indent);
          currentY += height;
          totalHeight += height;
        }
        // Add spacing after paragraph (minimal spacing)
        currentY += lineHeight * 0.15;
        totalHeight += lineHeight * 0.15;
      } else if (tagName === 'br') {
        currentY += lineHeight;
        totalHeight += lineHeight;
      } else if (tagName === 'ul') {
        // Process unordered list
        Array.from(node.children).forEach(li => {
          if (li.tagName.toLowerCase() === 'li') {
            processNode(li, indent + 15, '•');
          }
        });
      } else if (tagName === 'ol') {
        // Process ordered list
        Array.from(node.children).forEach((li, index) => {
          if (li.tagName.toLowerCase() === 'li') {
            processNode(li, indent + 15, `${index + 1}.`);
          }
        });
      } else if (tagName === 'li') {
        // Extract and render list item content
        const segments = extractFormattedText(node);
        if (segments.length > 0) {
          const height = renderFormattedSegments(segments, x, currentY, indent, listItemNumber);
          currentY += height;
          totalHeight += height;
        }
        // Add spacing after list item (minimal spacing)
        currentY += lineHeight * 0.2;
        totalHeight += lineHeight * 0.2;
      } else {
        // Process children for other block elements
        node.childNodes.forEach(child => processNode(child, indent, listItemNumber));
      }
    }
  };

  // Set initial font
  doc.setFontSize(fontSize);
  doc.setFont('helvetica', 'normal');

  // Process all child nodes
  tempDiv.childNodes.forEach(node => processNode(node));

  return totalHeight;
};

/**
 * Generate PDF from Treatment Plan data
 * @param {Object} treatmentPlan - The treatment plan data
 * @returns {jsPDF} - The generated PDF document
 */
export const generateTreatmentPlanPDF = async (treatmentPlan) => {
  console.log(`[Treatment Plan PDF] Generating PDF for:`, treatmentPlan);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Load and add logo
  let yPosition = 15;
  try {
    // Create a promise to load the logo image
    const logoPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = '/listenhear_logo.png';
    });

    const logoImg = await logoPromise;
    const logoWidth = 80;
    const logoHeight = (logoImg.height / logoImg.width) * logoWidth; // Maintain aspect ratio
    const logoX = (pageWidth - logoWidth) / 2;
    doc.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
    yPosition += logoHeight + 8;
  } catch (error) {
    console.warn('[Treatment Plan PDF] Could not load logo:', error);
    yPosition = 20; // Start without logo if it fails
  }

  // Helper function to add text with word wrap
  const addText = (text, x, y, options = {}) => {
    const {
      fontSize = 10,
      fontStyle = 'normal',
      align = 'left',
      maxWidth = pageWidth - 40,
    } = options;

    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);

    if (align === 'center') {
      doc.text(text, pageWidth / 2, y, { align: 'center', maxWidth });
    } else {
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y);
      return lines.length * (fontSize * 0.4); // Return approximate height
    }
    return fontSize * 0.4;
  };

  // Header
  addText('Speech & Language Treatment Plan', pageWidth / 2, yPosition, {
    fontSize: 16,
    fontStyle: 'bold',
    align: 'center',
  });
  yPosition += 8;

  // Use clinic address if available, otherwise use default Hearsay address
  let clinicAddress = '44 Main St. E., Milton, ON L9T 1N3 • Tel: (905) 875-3345 • Fax: (905) 875-0343';
  let clinicEmail = 'milton@hearsay.ca';

  if (treatmentPlan.clinicId) {
    const clinic = treatmentPlan.clinicId;
    console.log(`[Treatment Plan PDF] Using clinic data:`, clinic);

    if (clinic.address || clinic.phoneNumber) {
      const addressParts = [];
      if (clinic.address) addressParts.push(clinic.address);
      if (clinic.phoneNumber) addressParts.push(`Tel: ${clinic.phoneNumber}`);
      clinicAddress = addressParts.join(' • ');
    }

    if (clinic.email) {
      clinicEmail = clinic.email;
    }
  }

  addText(clinicAddress, pageWidth / 2, yPosition, {
    fontSize: 9,
    align: 'center',
  });
  yPosition += 5;

  addText(`e-mail: ${clinicEmail}`, pageWidth / 2, yPosition, {
    fontSize: 9,
    align: 'center',
  });
  yPosition += 10;

  // Add a line separator
  doc.setLineWidth(0.5);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 8;

  // Basic Information Section
  const formatDate = (date) => {
    if (!date) return 'N/A';
    // Parse date string and use UTC to avoid timezone offset issues
    const dateStr = typeof date === 'string' ? date.split('T')[0] : date.toISOString().split('T')[0];
    const [year, month, day] = dateStr.split('-');
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  };

  // Client Information
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const leftColumn = 20;
  const rightColumn = 110;
  const lineHeight = 6; // For basic client info rows
  const sectionLineHeight = 10 * 1.5; // For section spacing (fontSize * lineHeightMultiplier)

  // Create table header for client info
  doc.text(`Client Name: ${treatmentPlan.clientName || 'N/A'}`, leftColumn, yPosition);
  doc.text(`Date: ${formatDate(treatmentPlan.date)}`, rightColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`D.O.B.: ${formatDate(treatmentPlan.dateOfBirth)}`, leftColumn, yPosition);
  doc.text(`C.A.: ${treatmentPlan.chronologicalAge || 'N/A'}`, rightColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`Grade: ${treatmentPlan.grade || 'N/A'}`, leftColumn, yPosition);
  doc.text(`School: ${treatmentPlan.school || 'N/A'}`, rightColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`Speech-Language Pathologist: ${treatmentPlan.speechLanguagePathologist || 'N/A'}`, leftColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`Clinician(s): ${treatmentPlan.clinicians || 'N/A'}`, leftColumn, yPosition);
  yPosition += lineHeight + 2;

  // Check if we need a new page
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  // Current Communication Status
  if (treatmentPlan.currentCommunicationStatus) {
    doc.setFont('helvetica', 'bold');
    doc.text('Current Communication Status:', leftColumn, yPosition);
    yPosition += 4;

    doc.setFont('helvetica', 'normal');
    const statusHeight = renderHtmlToPdf(
      doc,
      treatmentPlan.currentCommunicationStatus,
      leftColumn,
      yPosition,
      { fontSize: 10, maxWidth: pageWidth - 40 }
    );
    yPosition += statusHeight + 3;
  }

  // Check if we need a new page
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  // Treatment Plan
  if (treatmentPlan.treatmentPlan) {
    doc.setFont('helvetica', 'bold');
    doc.text('Treatment Plan:', leftColumn, yPosition);
    yPosition += 4;

    doc.setFont('helvetica', 'normal');
    const planHeight = renderHtmlToPdf(
      doc,
      treatmentPlan.treatmentPlan,
      leftColumn,
      yPosition,
      { fontSize: 10, maxWidth: pageWidth - 40 }
    );
    yPosition += planHeight + 3;
  }

  // Check if we need a new page before the goals
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  // Treatment Goals
  doc.setFont('helvetica', 'bold');
  doc.text('Treatment Goals', leftColumn, yPosition);
  yPosition += 4;

  if (treatmentPlan.goals && treatmentPlan.goals.length > 0) {
    doc.setFont('helvetica', 'normal');

    treatmentPlan.goals.forEach((goal, index) => {
      // Check if we need a new page for this goal
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      // Goal number - ALWAYS use array index + 1 for sequential numbering (1, 2, 3, 4...)
      // Never use goal.id as it may contain timestamp values from Date.now()
      const goalNumber = index + 1;
      doc.setFont('helvetica', 'bold');
      doc.text(`${goalNumber}.`, leftColumn, yPosition);

      // Goal description
      doc.setFont('helvetica', 'normal');
      const goalHeight = renderHtmlToPdf(
        doc,
        goal.description,
        leftColumn + 5,
        yPosition,
        { fontSize: 10, maxWidth: pageWidth - 45 }
      );
      // Minimal spacing between goals
      yPosition += Math.max(goalHeight, 6) + 2;
    });
  } else {
    doc.setFont('helvetica', 'normal');
    doc.text('No goals specified', leftColumn, yPosition);
    yPosition += lineHeight;
  }

  yPosition += 8;

  // Footer - Signature Section
  if (yPosition > 240) {
    doc.addPage();
    yPosition = 20;
  }

  yPosition += 5;
  doc.setLineWidth(0.3);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 6;

  // Display speech-language pathologist if provided
  if (treatmentPlan.speechLanguagePathologist) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');

    // Determine qualifications to display
    let qualifications = treatmentPlan.speechLanguagePathologistQualifications;

    // If qualifications not stored in treatment plan, look up from populated user data
    if (!qualifications && treatmentPlan.speechLanguagePathologistId) {
      qualifications = treatmentPlan.speechLanguagePathologistId.invoiceDescriptor;
      console.log(`[Treatment Plan PDF] Looking up qualifications from user: ${qualifications}`);
    }

    // Use found qualifications or default to "Reg. CASLPO"
    const slpText = qualifications
      ? `${treatmentPlan.speechLanguagePathologist}, ${qualifications}`
      : `${treatmentPlan.speechLanguagePathologist}, Reg. CASLPO`;

    addText(slpText, pageWidth / 2, yPosition, {
      fontSize: 9,
      fontStyle: 'bold',
      align: 'center',
    });
    yPosition += 4;

    doc.setFont('helvetica', 'normal');
    addText('Speech-Language Pathologist', pageWidth / 2, yPosition, {
      fontSize: 9,
      align: 'center',
    });
    yPosition += 6;
  }

  // Add "c.: parent(s), client file"
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('c.: parent(s), client file', leftColumn, yPosition);
  yPosition += 6;

  // Add date of report
  doc.text(`(date of report/ ${formatDate(treatmentPlan.date)})`, leftColumn, yPosition);

  return doc;
};

/**
 * Download Treatment Plan as PDF
 * @param {Object} treatmentPlan - The treatment plan data
 * @param {string} filename - Optional custom filename
 */
export const downloadTreatmentPlanPDF = async (treatmentPlan, filename) => {
  const doc = await generateTreatmentPlanPDF(treatmentPlan);
  const defaultFilename = filename || `treatment-plan-${treatmentPlan.clientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(defaultFilename);
  console.log(`[Treatment Plan PDF] Downloaded: ${defaultFilename}`);
};
